import { Box, Stack } from "@repo/ui/core";
import { motion } from "framer-motion";
import { SlideTitle } from "@components/SlideTitle";

type Gate = {
  id: string;
  label: string;
  full: string;
  color: string;
  description: string;
  tests: string;
};

const GATES: Gate[] = [
  {
    id: "treble",
    label: "Treble",
    full: "Vendor / Framework Separation",
    color: "#4ade80",
    description: "Validates the HAL interface boundary between vendor and framework code",
    tests: "Interface conformance, HIDL/AIDL contract tests",
  },
  {
    id: "trout",
    label: "Trout",
    full: "Automotive Treble Extension",
    color: "#22d3ee",
    description: "Automotive-specific separation testing for vehicle HAL and audio routing",
    tests: "VHAL property contracts, audio policy validation",
  },
  {
    id: "cts",
    label: "CTS",
    full: "Compatibility Test Suite",
    color: "#60a5fa",
    description: "Google's compatibility gate — 1M+ tests ensuring API conformance across devices",
    tests: "API behavior, permissions model, UI rendering",
  },
  {
    id: "vts",
    label: "VTS",
    full: "Vendor Test Suite",
    color: "#a78bfa",
    description: "Forward compatibility validation for vendor implementations against new frameworks",
    tests: "HAL version contracts, kernel interface tests",
  },
];

export function ComplianceGatesSlide() {
  const svgW = 1100;
  const cardW = 240;
  const cardH = 170;
  const cardGap = 20;
  const totalW = cardW * GATES.length + cardGap * (GATES.length - 1);
  const startX = (svgW - totalW) / 2;
  const cardsY = 10;

  const calloutY = cardsY + cardH + 30;
  const calloutH = 70;
  const calloutW = totalW;
  const calloutX = startX;

  const svgH = calloutY + calloutH + 10;

  return (
    <Box w="100%" maw={1400} px="xl">
      <Stack gap="md">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <SlideTitle
            title="Compliance Gates"
            subtitle="Shift-left testing strategy — run compliance early in emulators, validate on hardware before release"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <svg
            viewBox={`0 0 ${svgW} ${svgH}`}
            width="100%"
            style={{ display: "block", maxHeight: "62vh" }}
            aria-label="Compliance gate cards showing Treble, Trout, CTS, and VTS"
          >
            {GATES.map((gate, i) => {
              const x = startX + i * (cardW + cardGap);
              return (
                <motion.g
                  key={gate.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.12, duration: 0.4 }}
                >
                  <rect
                    x={x}
                    y={cardsY}
                    width={cardW}
                    height={cardH}
                    rx={10}
                    fill="rgba(255,255,255,0.025)"
                    stroke={gate.color}
                    strokeWidth={1.5}
                    strokeOpacity={0.2}
                  />
                  <rect x={x} y={cardsY} width={cardW} height={3} rx={1.5} fill={gate.color} opacity={0.6} />

                  {/* Label */}
                  <text
                    x={x + 16}
                    y={cardsY + 28}
                    fontSize={16}
                    fill={gate.color}
                    fontWeight={700}
                    fontFamily="inherit"
                  >
                    {gate.label}
                  </text>
                  <text
                    x={x + 16}
                    y={cardsY + 44}
                    fontSize={9.5}
                    fill="rgba(255,255,255,0.4)"
                    fontWeight={500}
                    fontFamily="inherit"
                  >
                    {gate.full}
                  </text>

                  {/* Divider */}
                  <line
                    x1={x + 16}
                    y1={cardsY + 54}
                    x2={x + cardW - 16}
                    y2={cardsY + 54}
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth={1}
                  />

                  {/* Description */}
                  <foreignObject x={x + 16} y={cardsY + 60} width={cardW - 32} height={50}>
                    <div
                      // @ts-expect-error xmlns
                      xmlns="http://www.w3.org/1999/xhtml"
                      style={{
                        fontSize: 10,
                        lineHeight: 1.4,
                        color: "rgba(255,255,255,0.6)",
                        fontFamily: "inherit",
                      }}
                    >
                      {gate.description}
                    </div>
                  </foreignObject>

                  {/* Test scope */}
                  <foreignObject x={x + 16} y={cardsY + 118} width={cardW - 32} height={40}>
                    <div
                      // @ts-expect-error xmlns
                      xmlns="http://www.w3.org/1999/xhtml"
                      style={{
                        fontSize: 9,
                        lineHeight: 1.4,
                        color: "rgba(255,255,255,0.35)",
                        fontFamily: "'Geist Mono', monospace",
                      }}
                    >
                      {gate.tests}
                    </div>
                  </foreignObject>
                </motion.g>
              );
            })}

            {/* Snapshot acceleration callout */}
            <motion.g
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.4 }}
            >
              <rect
                x={calloutX}
                y={calloutY}
                width={calloutW}
                height={calloutH}
                rx={8}
                fill="rgba(74,222,128,0.04)"
                stroke="rgba(74,222,128,0.15)"
                strokeWidth={1}
              />
              <text
                x={calloutX + 20}
                y={calloutY + 24}
                fontSize={12}
                fill="#4ade80"
                fontWeight={700}
                fontFamily="inherit"
              >
                Accelerated by Snapshots
              </text>
              <foreignObject x={calloutX + 20} y={calloutY + 32} width={calloutW - 40} height={32}>
                <div
                  // @ts-expect-error xmlns
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{
                    fontSize: 10.5,
                    lineHeight: 1.5,
                    color: "rgba(255,255,255,0.55)",
                    fontFamily: "inherit",
                  }}
                >
                  These compliance suites will be greatly accelerated by snapshot-based emulators — the same infrastructure that powers the build system.
                  We are actively developing each gate and want to understand your specific requirements during the pilot.
                </div>
              </foreignObject>
            </motion.g>
          </svg>
        </motion.div>
      </Stack>
    </Box>
  );
}
